import { useMemo } from 'react';
import type {
  PerkPreset,
  PerkPresetItem,
} from '../../domain/entities/catalog.entity';
import {
  useApplyPerkPresetItemBatch,
  usePerkPresetItems,
} from '../hooks/use-perk-presets';
import { useAllPerkGroups } from '../hooks/use-perks';
import { describeGrantPolicyValues, grantPolicyLabel } from '../labels';
import { indexPerkForms } from '../perk-index';
import { PerkAssociationEditor } from './PerkAssociationEditor';
import type {
  ExistingPerkRow,
  PerkAssociationSeedRow,
  PerkAssociationTarget,
} from './PerkAssociationEditor';
import { ScopeBadgeList } from '@/shared/scopes/ScopeBadge';

type PerkPresetItemRow = ExistingPerkRow & { item: PerkPresetItem };

interface PerkPresetItemsEditorModalProps {
  preset: PerkPreset;
  open: boolean;
  onClose: () => void;
  /** Criar preset a partir de um existente (§8 do plano): itens da origem. */
  initialAdditions?: PerkAssociationSeedRow[];
}

/**
 * O alvo **preset** do editor. Mesmas regras do produto menos a política de
 * inadimplência, que é exclusiva da associação a produto. Editar um preset não
 * mexe em nada já concedido ou associado a partir dele: o preset é clonado no
 * uso, não consultado depois.
 */
export function PerkPresetItemsEditorModal({
  preset,
  open,
  onClose,
  initialAdditions,
}: PerkPresetItemsEditorModalProps) {
  const { data: items = [], isLoading } = usePerkPresetItems(preset.id);
  const { data: groups } = useAllPerkGroups();
  const { mutateAsync } = useApplyPerkPresetItemBatch(preset.id);

  const perkIndex = useMemo(() => indexPerkForms(groups), [groups]);

  const target: PerkAssociationTarget<PerkPresetItemRow> = useMemo(
    () => ({
      title: `Perks do preset "${preset.name}"`,
      description:
        'O pacote que este preset despeja quando alguém o aplica. Nada é gravado até aplicar.',
      duplicatesAllowed: false,
      supportsTargetScopes: true,
      removal: { label: 'Remover', needsReason: false },
      existing: items.map((item) => ({
        id: item.id,
        perkId: item.perkId,
        item,
      })),
      existingLoading: isLoading,
      editableValues: (row) => ({
        durationInMilliseconds: row.item.durationInMilliseconds,
        maxUsages: row.item.maxUsages,
        renewEveryInMilliseconds: row.item.renewEveryInMilliseconds,
        targetScopes: row.item.targetScopes,
        // O preset não guarda política de inadimplência nem entrega por ciclo;
        // os dois só existem no alvo produto e são ignorados no corpo da
        // request daqui.
        overduePolicy: 'keep_active' as const,
        deliversPerCycle: false,
      }),
      renderExisting: ({ item }) => {
        const resolved = perkIndex.get(item.perkId);
        return (
          <div className="space-y-0.5">
            <span className="text-(--color-text-primary) block text-sm">
              {resolved?.group.name ?? resolved?.group.key ?? item.perkId}
            </span>
            <span className="text-(--color-text-secondary) block text-xs">
              {resolved ? grantPolicyLabel(resolved.form.grantPolicyKind) : '—'}
              {' · '}
              {describeGrantPolicyValues(item)}
            </span>
            {item.targetScopes.length > 0 && (
              <ScopeBadgeList ids={item.targetScopes} />
            )}
          </div>
        );
      },
      submit: async ({ add, remove }) => {
        await mutateAsync({
          add: add.map((addition) => ({
            perkId: addition.perkId,
            ...addition.grantPolicyParams,
            ...(addition.targetScopes.length > 0
              ? { targetScopes: addition.targetScopes }
              : {}),
          })),
          remove: remove.map((row) => row.id),
        });
      },
    }),
    [preset.name, items, isLoading, perkIndex, mutateAsync],
  );

  return (
    <PerkAssociationEditor
      open={open}
      onClose={onClose}
      target={target}
      initialAdditions={initialAdditions}
    />
  );
}
